import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shareToWhatsApp } from "@/lib/share";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { MessageCircle, Mail, Globe, Loader2, Truck, Building2, Send } from "lucide-react";

const STATE_LABELS = { missing: "Eksik", wrong: "Yanlış", damaged: "Hasarlı", returned: "İade", exchange: "Değişim" };

/**
 * Forward a warehouse discrepancy / return report (with photos in the PDF) to a
 * supplier (via WhatsApp / Mail / B2B) or to purchasing (in-app notification).
 * Props: open, onOpenChange, request, problems (line[] with state!=ok), onDone?
 */
export default function DiscrepancyReportDialog({ open, onOpenChange, request, problems = [], onDone }) {
  const { user } = useAuth();
  const [recipient, setRecipient] = useState("supplier");
  const [suppliers, setSuppliers] = useState([]);
  const [supplierId, setSupplierId] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/suppliers");
        setSuppliers(data);
      } catch (_) {}
    })();
    setRecipient("supplier");
    setSupplierId(request?.supplier_id || "");
    setNote("");
  }, [open, request]);

  // Pre-fetch the discrepancy PDF as soon as the dialog opens so the WhatsApp
  // button can call navigator.share() SYNCHRONOUSLY on click (with the file
  // ready) — this opens the native "Paylaş" sheet (pick WhatsApp) instead of a
  // wa.me browser tab, and avoids losing the user-gesture/transient activation.
  useEffect(() => {
    if (!open || !request?.id) { setPdfFile(null); return; }
    let cancelled = false;
    setPdfLoading(true);
    (async () => {
      try {
        const f = await fetchPdf("Tedarikçi");
        if (!cancelled) setPdfFile(f);
      } catch (_) {
        if (!cancelled) setPdfFile(null);
      } finally {
        if (!cancelled) setPdfLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, request?.id]);

  const supplier = suppliers.find((s) => s.id === supplierId);

  const buildList = () =>
    problems
      .map((it, i) => {
        let line = `${i + 1}. ${it.product_code} — ${it.product_name} • ${STATE_LABELS[it.state] || it.state}`;
        if (it.state === "exchange" && it.exchange_to_code) {
          line += ` → Değişim: ${it.exchange_to_code} ${it.exchange_to_name || ""}`.trimEnd();
        }
        if (it.note) line += ` (${it.note})`;
        return line;
      })
      .join("\n");

  const buildMessageText = (supName) =>
    `Merhaba ${supName},\n\n*${request.request_no}* siparişinde uyumsuzluk/iade/değişim tespit edildi:\n${buildList()}\n\nDetaylar ve fotoğraflar ekteki PDF'tedir.`;

  const fetchPdf = async (recipientLabel) => {
    const { data } = await api.get(
      `/requests/${request.id}/discrepancy-pdf?recipient=${encodeURIComponent(recipientLabel)}`,
      { responseType: "blob" }
    );
    return new File([data], `uyumsuzluk-${request.request_no}.pdf`, { type: "application/pdf" });
  };

  const downloadFile = (file) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement("a");
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  // Build the WhatsApp deep-link synchronously (fallback when the native share
  // sheet / Web Share with files is unavailable).
  const buildWaUrl = (supName, supPhone) => {
    const waNumber = (supPhone || "").replace(/[^\d]/g, "");
    const message = buildMessageText(supName);
    return waNumber
      ? `https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
  };

  const buildMailUrl = (supName, supEmail) => {
    const subject = `Sipariş Uyumsuzluk/İade/Değişim Bildirimi: ${request.request_no}`;
    const body =
      `Merhaba ${supName},\n\n` +
      `${request.request_no} numaralı siparişte aşağıdaki uyumsuzluk/iade/değişim durumu tespit edilmiştir (detay ve fotoğraflar ekteki PDF'te):\n${buildList()}\n\n` +
      `Konuyla ilgilenmenizi rica ederiz.\n\n` +
      `Bildiren: ${user?.name || ""}` + (user?.email ? ` <${user.email}>` : "");
    const params = new URLSearchParams();
    params.set("subject", subject);
    params.set("body", body);
    if (user?.email) params.set("cc", user.email);
    return `mailto:${encodeURIComponent(supEmail || "")}?${params.toString().replace(/\+/g, "%20")}`;
  };

  const recordDiscrepancy = async (channel, supName) => {
    await api.post(`/requests/${request.id}/report-discrepancy`, {
      recipient: "supplier",
      channel,
      supplier_id: supplierId,
      supplier_name: supName,
      note,
    });
  };

  // WhatsApp: open the native OS share sheet (Windows "Paylaş" / mobile share)
  // with the PDF attached so the user picks WhatsApp directly. Must be called
  // synchronously within the click (the PDF is pre-fetched on open).
  const shareWhatsApp = async () => {
    if (!supplierId) { toast.error("Lütfen tedarikçi seçin"); return; }
    const supName = supplier?.name || request.supplier_name || "";
    const message = buildMessageText(supName);

    // Open the native OS share sheet (Windows "Paylaş" / mobile) with the PDF
    // attached. When file sharing is unavailable, the sheet still opens with the
    // text (PDF downloaded), and only as a last resort do we fall back to wa.me.
    await shareToWhatsApp({
      text: message,
      title: `Uyumsuzluk ${request.request_no}`,
      file: pdfFile,
      waUrl: buildWaUrl(supName, supplier?.phone),
      downloadName: `uyumsuzluk-${request.request_no}.pdf`,
    });

    setBusy("whatsapp");
    try {
      await recordDiscrepancy("whatsapp", supName);
      toast.success(`${supName} tedarikçisine uyumsuzluk/iade/değişim bildirimi hazırlandı (WhatsApp)`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  const sendToSupplier = async (channel) => {
    if (!supplierId) { toast.error("Lütfen tedarikçi seçin"); return; }
    const supName = supplier?.name || request.supplier_name || "";

    // Mail: open the default mail client synchronously (within the click gesture).
    if (channel === "mail") {
      window.open(buildMailUrl(supName, supplier?.email), "_blank");
    }

    setBusy(channel);
    try {
      await recordDiscrepancy(channel, supName);
      // Generate + download the PDF so the user can attach it to the message.
      try { downloadFile(pdfFile || await fetchPdf("Tedarikçi")); } catch (_) {}
      const label = { mail: "Mail", b2b: "B2B" }[channel];
      toast.success(`${supName} tedarikçisine uyumsuzluk/iade/değişim bildirimi iletildi (${label})`);
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  const sendToPurchasing = async () => {
    setBusy("app");
    try {
      await api.post(`/requests/${request.id}/report-discrepancy`, {
        recipient: "purchasing",
        channel: "app",
        note,
      });
      try { downloadFile(await fetchPdf("Satınalma")); } catch (_) {}
      toast.success("Satınalma ekibine bildirim iletildi");
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "İşlem başarısız");
    } finally {
      setBusy("");
    }
  };

  const RecipientButton = ({ value, Icon, label }) => (
    <button
      type="button"
      data-testid={`disc-recipient-${value}`}
      onClick={() => setRecipient(value)}
      className={`flex-1 flex items-center justify-center gap-2 h-11 rounded-md border text-sm font-semibold transition-colors
        ${recipient === value ? "bg-rose-600 text-white border-rose-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="discrepancy-report-dialog" className="max-w-md p-4 sm:p-6 max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Send className="h-5 w-5 text-rose-600" /> Durumu İlet
          </DialogTitle>
          <DialogDescription className="text-xs sm:text-sm">
            <span className="font-semibold text-slate-700">{problems.length}</span> sorunlu/iade kalem için fotoğraflı PDF raporu tedarikçiye veya satınalmaya iletin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Alıcı</Label>
            <div className="mt-1.5 flex gap-2">
              <RecipientButton value="supplier" Icon={Truck} label="Tedarikçi" />
              <RecipientButton value="purchasing" Icon={Building2} label="Satınalma" />
            </div>
          </div>

          {/* Problem item preview */}
          <div className="border border-slate-100 rounded-md overflow-hidden max-h-36 overflow-y-auto thin-scroll">
            {problems.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 first:border-t-0 items-center text-xs">
                <div className="col-span-4 font-medium text-slate-900">{it.product_code}</div>
                <div className="col-span-5 text-slate-600 truncate">{it.product_name}</div>
                <div className="col-span-3 text-right font-semibold text-rose-600">{STATE_LABELS[it.state] || it.state}</div>
              </div>
            ))}
          </div>

          {recipient === "supplier" && (
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Tedarikçi</Label>
              <Select value={supplierId} onValueChange={setSupplierId}>
                <SelectTrigger data-testid="disc-supplier-select" className="mt-1.5 h-11">
                  <SelectValue placeholder="Tedarikçi seçin" />
                </SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`disc-supplier-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Ek Not (opsiyonel)</Label>
            <Textarea
              data-testid="disc-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Mesaja eklenecek açıklama..."
              className="mt-1.5 text-sm min-h-[60px]"
            />
          </div>

          {recipient === "supplier" ? (
            <div className="grid grid-cols-3 gap-2 pt-1">
              <Button
                data-testid="disc-whatsapp-btn"
                onClick={shareWhatsApp}
                disabled={!!busy || !supplierId || pdfLoading}
                className="h-11 gap-1.5 bg-[#25D366] hover:bg-[#128C7E] text-white"
              >
                {(busy === "whatsapp" || pdfLoading) ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageCircle className="h-4 w-4" />} {pdfLoading ? "Hazırlanıyor" : "WhatsApp"}
              </Button>
              <Button
                data-testid="disc-mail-btn"
                onClick={() => sendToSupplier("mail")}
                disabled={!!busy || !supplierId}
                variant="outline"
                className="h-11 gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
              >
                {busy === "mail" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Mail
              </Button>
              <Button
                data-testid="disc-b2b-btn"
                onClick={() => sendToSupplier("b2b")}
                disabled={!!busy || !supplierId}
                variant="outline"
                className="h-11 gap-1.5 border-indigo-200 text-indigo-700 hover:bg-indigo-50"
              >
                {busy === "b2b" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />} B2B
              </Button>
            </div>
          ) : (
            <Button
              data-testid="disc-purchasing-btn"
              onClick={sendToPurchasing}
              disabled={!!busy}
              className="w-full h-11 gap-2 bg-rose-600 hover:bg-rose-700 text-white"
            >
              {busy === "app" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Satınalmaya İlet
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
